import * as React from 'react';
import { LuCheck, LuClipboard } from 'react-icons/lu';

import { cn } from '@/utils/cn';
import { logger } from 'utils';
import { useClipboard } from 'utils/hooks';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export interface CopiableGuidProps {
    guid: string | undefined;
    className?: string;
    showFullGuid?: boolean;
    showBorder?: boolean;
    showDigits?: 'firstFour' | 'lastFour';
    shortenedPrefix?: string;
}

export const CopiableGuid = ({
    guid,
    className,
    shortenedPrefix,
    showFullGuid = false,
    showBorder = true,
    showDigits = 'firstFour',
}: CopiableGuidProps) => {
    const { isCopied, copy } = useClipboard({
        timeoutMs: 1000,
        onError: (error) => {
            logger.error('Failed to copy text:', error);
        },
    });

    if (!guid) {
        return null;
    }

    const handleCopy = (event: React.MouseEvent) => {
        void copy(guid, { event });
    };

    const shownDigits = showDigits === 'firstFour' ? guid.slice(0, 4) : guid.slice(-4);
    const displayText = showFullGuid ? guid : (shortenedPrefix || '') + shownDigits;
    return (
        <TooltipProvider delayDuration={0}>
            <div
                className={cn(
                    'text-annotation inline-flex items-center overflow-hidden rounded-md font-mono text-xs',
                    showBorder && 'h-[40px] border border-gray-300',
                    className,
                )}
            >
                <Tooltip key={isCopied ? 'copied' : 'normal'} open={isCopied ? true : undefined}>
                    <TooltipTrigger asChild>
                        <div
                            className="inline-flex cursor-pointer items-center justify-center gap-2 p-1 pl-2"
                            onClick={handleCopy}
                        >
                            <div className="text-center whitespace-nowrap">{showFullGuid ? guid : displayText}</div>
                            <div
                                className={cn(
                                    'hover:bg-accent rounded p-1 hover:text-white',
                                    showBorder && 'border-annotation flex h-8 w-8 items-center justify-center',
                                )}
                            >
                                {isCopied ? <LuCheck className="h-3 w-3" /> : <LuClipboard className="h-3 w-3" />}
                            </div>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>{isCopied ? 'Copied!' : showFullGuid ? 'Copy GUID' : guid}</TooltipContent>
                </Tooltip>
            </div>
        </TooltipProvider>
    );
};
